import { FunctionComponent } from 'react';
import cn from 'classnames';
import { Design, ProductType, Shop } from '@/types';
import { usePriceFormatter } from '@/hooks/usePrice';
import { isFreeDesign } from '@/model/design/isFreeDesign';
import { isOwnerOfShopAndDesign } from '@/model/pricing/isOwnerOfShopAndDesign';

interface Props {
  productType: ProductType;
  design: Design;
  context: Shop;
  zeroValue: string;
  className?: string;
  zeroClassName?: string;
  isMarketplaceCategoryActive: boolean;
}

const getDesignPrice = (
  design: Design,
  context: Shop,
  isMarketplaceCategoryActive: boolean,
  productType: ProductType
) => {
  if (isMarketplaceCategoryActive) {
    // MP designs are either
    // * free
    // * belong to the same  user (only MdsPriceStrategy)
    // * priced using priceStrategy.designBasedCommission.amount
    // * if - for whatever reason there is no user on the design, make it not free

    const priceStrategy = context.priceStrategy;

    let commissionType: { amount: number } | undefined;
    if ('designBasedCommission' in priceStrategy) {
      commissionType = priceStrategy.designBasedCommission;
    } else if ('productTypeBasedCommissions' in priceStrategy) {
      const productTypeBasedCommissions = priceStrategy.productTypeBasedCommissions;
      commissionType =
        productTypeBasedCommissions[productType.id] || productTypeBasedCommissions.DEFAULT;
    }

    const designCommission = commissionType?.amount || 0;

    if (
      designCommission > 0 &&
      !isFreeDesign(design) && // design is not a free design
      !isOwnerOfShopAndDesign(context, design) // design is not owned by the shop owner
    ) {
      return designCommission;
    }
  }

  return 0;
};

export const DesignPrice: FunctionComponent<Props> = ({
  design,
  context,
  zeroValue,
  className,
  zeroClassName,
  isMarketplaceCategoryActive,
  productType,
}) => {
  const price = getDesignPrice(design, context, isMarketplaceCategoryActive, productType);
  const classes = cn(className, price === 0 && zeroClassName);
  const priceFormatter = usePriceFormatter({ zeroValue });

  return <span className={classes}>{priceFormatter(price)}</span>;
};
